const TRANSIENT_FIELDS = [
    'snapshotTransport',
    'baseSnapshotRevision',
    'changes',
    'arrayPatches',
    'removedFields',
    'preparedPending',
    'preparedSignal',
    'isQuickResponse',
    'updated'
];

function cleanSnapshot(snapshot) {
    const result = { ...(snapshot || {}) };
    TRANSIENT_FIELDS.forEach((field) => delete result[field]);
    return result;
}

export function applyPreparedSnapshot(currentSnapshot, payload) {
    if (!payload || payload.error || !payload.id) return null;

    if (payload.snapshotTransport === 'delta-v1' || payload.snapshotTransport === 'delta-v2') {
        if (!currentSnapshot || currentSnapshot.snapshotRevision !== payload.baseSnapshotRevision) return null;
        const next = { ...cleanSnapshot(currentSnapshot), ...(payload.changes || {}) };
        if (payload.snapshotTransport === 'delta-v2') {
            for (const field of Object.keys(payload.arrayPatches || {})) {
                const nextItems = applyKeyedArrayPatch(next[field], payload.arrayPatches[field]);
                if (!nextItems) return null;
                next[field] = nextItems;
            }
        }
        (payload.removedFields || []).forEach((field) => delete next[field]);
        next.snapshotRevision = payload.snapshotRevision;
        return next;
    }

    if (!Array.isArray(payload.actions)) return null;
    return cleanSnapshot(payload);
}

function applyKeyedArrayPatch(baseItems, patch) {
    if (
        !Array.isArray(baseItems) ||
        !patch ||
        !['id', 'code'].includes(patch.key) ||
        !Array.isArray(patch.upserts) ||
        !Array.isArray(patch.removedKeys) ||
        !Array.isArray(patch.order)
    )
        return null;
    const key = patch.key;
    const baseKeys = baseItems.map((item) => item && item[key]);
    if (baseKeys.some((value) => value === undefined || value === null) || new Set(baseKeys).size !== baseKeys.length) return null;

    const byKey = new Map(baseItems.map((item) => [item[key], item]));
    const upsertKeys = patch.upserts.map((item) => item && item[key]);
    if (upsertKeys.some((value) => value === undefined || value === null) || new Set(upsertKeys).size !== upsertKeys.length) return null;
    for (const item of patch.upserts) {
        if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
        byKey.set(item[key], item);
    }
    patch.removedKeys.forEach((value) => byKey.delete(value));

    if (new Set(patch.order).size !== patch.order.length || patch.order.length !== byKey.size) return null;
    const nextItems = [];
    for (const value of patch.order) {
        if (!byKey.has(value)) return null;
        nextItems.push(byKey.get(value));
    }
    return nextItems;
}

export function createCollectionSnapshotRequest(state = {}, options = {}) {
    const version = Math.max(1, Number(options.snapshotTransportVersion) || 1);
    if (options.forceFull || !state.snapshotRevision) return { snapshotTransportVersion: version };
    return {
        knownSnapshotRevision: state.snapshotRevision,
        knownRows: state.rowRevisions || {},
        snapshotTransportVersion: version
    };
}

export function createSnapshotRevisionManifest(items, options = {}) {
    const sourceItems = items instanceof Array ? items : [];
    const identityField = options.identityField || 'id';
    const revisionField = options.revisionField || 'snapshotRevision';
    const getSnapshot =
        typeof options.getSnapshot === 'function' ? options.getSnapshot : (item) => item;
    const getIdentity =
        typeof options.getIdentity === 'function'
            ? options.getIdentity
            : (snapshot) => snapshot?.[identityField];
    const isValid =
        typeof options.isValid === 'function'
            ? options.isValid
            : (snapshot) => Boolean(snapshot?.[revisionField]);
    const manifest = {};

    sourceItems.forEach((item) => {
        const snapshot = getSnapshot(item);
        const identity = getIdentity(snapshot, item);
        if (
            identity === undefined ||
            identity === null ||
            !isValid(snapshot, identity, item) ||
            !snapshot?.[revisionField]
        )
            return;
        manifest[String(identity)] = snapshot[revisionField];
    });
    return manifest;
}

export function getCollectionSnapshotReconciliation(currentRows, snapshotRows, options = {}) {
    const identityFields = (
        options.matchFields instanceof Array && options.matchFields.length > 0
            ? options.matchFields
            : [options.identityField || 'id']
    ).filter(Boolean);
    const getIdentity = (row) => {
        const field = identityFields.find(
            (name) => row?.[name] !== undefined && row?.[name] !== null
        );
        return field ? String(row[field]) : null;
    };
    const currentKeys = (currentRows instanceof Array ? currentRows : [])
        .filter((row) => row && !row.empty)
        .map(getIdentity)
        .filter((value) => value !== null);
    const snapshotKeys = (snapshotRows instanceof Array ? snapshotRows : [])
        .filter(Boolean)
        .map(getIdentity)
        .filter((value) => value !== null);
    const currentKeySet = new Set(currentKeys);
    const snapshotKeySet = new Set(snapshotKeys);
    const isCurrentComplete =
        currentKeys.length === snapshotKeys.length &&
        currentKeySet.size === currentKeys.length &&
        snapshotKeySet.size === snapshotKeys.length &&
        snapshotKeys.every((key) => currentKeySet.has(key));
    const shouldRepairBlankRender =
        currentKeys.length > 0 && options.renderedRowCount === 0;

    return {
        currentKeys,
        isCurrentComplete,
        shouldRepairBlankRender,
        shouldPreserveCurrent:
            options.preserveOnEmpty === true && currentKeys.length > 0 && snapshotKeys.length === 0
    };
}


export function applyCollectionSnapshot(currentState = {}, payload, options = {}) {
    if (!payload || payload.error || !payload.snapshotRevision) return null;
    const identityField = options.identityField || 'id';
    const rowRevisionField = options.rowRevisionField || '_snapshotRevision';
    // Reconciliation can finish after a socket has already invalidated the
    // request. Never mutate the caller-owned baseline while merely decoding a
    // response: the screen must be able to reject that response without its
    // current manifest/rows having changed behind its back.
    let rowsByKey = currentState.rowsByKey instanceof Map ? new Map(currentState.rowsByKey) : new Map();
    let rowRevisions =
        currentState.rowRevisions && typeof currentState.rowRevisions === 'object'
            ? { ...currentState.rowRevisions }
            : {};
    let changedRows = [];
    let removedKeys = [];

    if (payload.snapshotTransport === 'collection-not-modified') {
        if (!currentState.snapshotRevision || currentState.snapshotRevision !== payload.snapshotRevision) return null;
    } else if (payload.snapshotTransport === 'collection-full-v1') {
        if (!(payload.rows instanceof Array)) return null;
        const nextRows = new Map();
        const nextRevisions = {};
        for (const sourceRow of payload.rows) {
            if (!sourceRow || sourceRow[identityField] === undefined || sourceRow[identityField] === null) return null;
            const key = String(sourceRow[identityField]);
            const revision = sourceRow[rowRevisionField];
            if (!revision || nextRows.has(key)) return null;
            const row = { ...sourceRow };
            delete row[rowRevisionField];
            nextRows.set(key, row);
            nextRevisions[key] = revision;
        }
        // microbundle-crl does not reliably transpile iterator spread for the
        // legacy Web target: `[...map.values()]` becomes `[MapIterator]` and is
        // serialized/rendered as `[{}]`. Always materialize Map iterators with
        // Array.from in code shipped by ante-react-kit.
        removedKeys = Array.from(rowsByKey.keys()).filter((key) => !nextRows.has(String(key))).map(String);
        rowsByKey = nextRows;
        rowRevisions = nextRevisions;
        changedRows = Array.from(nextRows.values());
    } else if (payload.snapshotTransport === 'collection-delta-v1') {
        if (
            !currentState.snapshotRevision ||
            payload.baseSnapshotRevision !== currentState.snapshotRevision ||
            !(payload.upserts instanceof Array) ||
            !(payload.removedIds instanceof Array)
        )
            return null;
        const upsertKeys = new Set();
        for (const row of payload.upserts) {
            if (!row || row[identityField] === undefined || row[identityField] === null || !row[rowRevisionField])
                return null;
            const key = String(row[identityField]);
            if (upsertKeys.has(key)) return null;
            upsertKeys.add(key);
        }
        removedKeys = payload.removedIds.map(String);
        if (new Set(removedKeys).size !== removedKeys.length || removedKeys.some((key) => upsertKeys.has(key))) return null;
        let orderedKeys = null;
        if (payload.order !== undefined) {
            if (!(payload.order instanceof Array)) return null;
            orderedKeys = payload.order.map(String);
            const finalKeys = new Set(Array.from(rowsByKey.keys()).map(String));
            upsertKeys.forEach((key) => finalKeys.add(key));
            removedKeys.forEach((key) => finalKeys.delete(key));
            if (
                new Set(orderedKeys).size !== orderedKeys.length ||
                orderedKeys.length !== finalKeys.size ||
                orderedKeys.some((key) => !finalKeys.has(key))
            )
                return null;
        }

        changedRows = payload.upserts.map((sourceRow) => {
            const key = String(sourceRow[identityField]);
            const row = { ...sourceRow };
            rowRevisions[key] = row[rowRevisionField];
            delete row[rowRevisionField];
            rowsByKey.set(key, row);
            return row;
        });
        removedKeys.forEach((key) => {
            rowsByKey.delete(key);
            delete rowRevisions[key];
        });
        if (orderedKeys) rowsByKey = new Map(orderedKeys.map((key) => [key, rowsByKey.get(key)]));
    } else {
        return null;
    }

    return {
        rowsByKey,
        rowRevisions,
        snapshotRevision: payload.snapshotRevision,
        changedRows,
        removedKeys,
        rows: Array.from(rowsByKey.values()),
        snapshotTransport: payload.snapshotTransport,
        isFull: payload.snapshotTransport === 'collection-full-v1',
        isDelta: payload.snapshotTransport === 'collection-delta-v1',
        isNotModified: payload.snapshotTransport === 'collection-not-modified'
    };
}
